// Single entry point for sending a barcode label to a printer.
//
// Resolves *which* printer gets the label and *which* command language it
// speaks, then encodes the label accordingly. Without this, labels only ever
// worked on a dedicated TSC/TSPL printer; on the common setup (one ESC/POS USB
// printer doing receipts and labels) nothing printed at all.

import { getBarcodeSettings, LabelLanguage } from './barcode';
import { buildLabelTspl, LabelSpec } from './tspl';
import { buildLabelEscPos } from './escposLabel';
import {
  getSavedLabelPrinter,
  getSavedPrinter,
  isUsbAvailable,
  printBytesTo,
  SavedPrinter,
} from './usbPrinter';

export type LabelLang = 'tspl' | 'escpos';

export interface LabelTarget {
  printer: SavedPrinter;
  lang: LabelLang;
  /** True when the label is going to the receipt printer for want of a label one. */
  viaReceiptPrinter: boolean;
}

// Label printer slot first; otherwise the receipt printer, which is what most
// single-printer setups have configured. The saved language preference wins
// when it isn't 'auto'.
export function resolveLabelTarget(pref?: LabelLanguage): LabelTarget | null {
  if (!isUsbAvailable()) return null;
  const preference = pref ?? getBarcodeSettings().labelLanguage;
  const label = getSavedLabelPrinter();
  const receipt = getSavedPrinter();
  const printer = label ?? receipt;
  if (!printer) return null;
  const viaReceiptPrinter = !label && !!receipt;
  const auto: LabelLang = viaReceiptPrinter ? 'escpos' : 'tspl';
  return {
    printer,
    lang: preference === 'auto' ? auto : preference,
    viaReceiptPrinter,
  };
}

// True when a label can go to a USB printer (so callers know not to fall back
// to the browser print sheet).
export function labelPrintingAvailable(): boolean {
  return !!resolveLabelTarget();
}

export function buildLabelBytes(spec: LabelSpec, lang: LabelLang): Uint8Array {
  return lang === 'tspl' ? buildLabelTspl(spec) : buildLabelEscPos(spec);
}

// Encode + send one label spec. Throws when there is no USB target or the
// transfer fails, so callers can fall back to browser printing.
export async function printLabelSpec(spec: LabelSpec): Promise<void> {
  const target = resolveLabelTarget();
  if (!target) throw new Error('No USB printer selected for barcode labels');
  await printBytesTo(target.printer, buildLabelBytes(spec, target.lang));
}
